import React from 'react';
import { cn } from '../../utils/cn';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/copy-button';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Download, Trash2, ExternalLink, Copy, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

export interface UrlItem {
  id: string;
  url: string;
  loading: boolean;
  error: string | null;
  markdown: string | null;
  title: string | null;
}

interface URLListItemProps {
  item: UrlItem;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onDownload: (id: string) => void;
  onRemove: (id: string) => void;
  onFetch: (id: string) => void;
}

export const URLListItem: React.FC<URLListItemProps> = ({
  item,
  isSelected,
  onSelect,
  onDownload,
  onRemove,
  onFetch
}) => {
  // 获取友好的显示标题
  const displayTitle = item.title || item.url.replace(/^https?:\/\//, '').split('/')[0];
  
  // 获取状态相关信息
  const getStatusInfo = () => {
    if (item.loading) {
      return {
        text: "解析中...",
        color: "text-blue-500",
        icon: <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />,
        badge: <Badge variant="default" className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> 解析中</Badge>
      };
    } else if (item.error) {
      return {
        text: item.error,
        color: "text-red-500",
        icon: <AlertCircle className="h-3.5 w-3.5 mr-1" />,
        badge: <Badge variant="destructive" className="flex items-center gap-1"><AlertCircle className="h-3 w-3" /> 解析失败</Badge>
      };
    } else if (item.markdown) {
      return {
        text: "已解析",
        color: "text-green-500",
        icon: <CheckCircle className="h-3.5 w-3.5 mr-1" />,
        badge: <Badge variant="secondary" className="flex items-center gap-1"><CheckCircle className="h-3 w-3" /> 已解析</Badge>
      };
    }
    return {
      text: "未解析",
      color: "text-gray-500",
      icon: null,
      badge: <Badge variant="outline">未解析</Badge>
    };
  };
  
  const statusInfo = getStatusInfo();
  
  // 计算文件大小信息
  const fileSize = item.markdown 
    ? `${Math.round(item.markdown.length / 1024 * 10) / 10} KB` 
    : '';
  
  return (
    <Card 
      className={cn(
        "w-full transition-all duration-200 hover:shadow-md overflow-hidden",
      )}
      onClick={() => onSelect(item.id)}
    >
      <CardContent className="p-0">
        <div className="flex flex-col">
          {/* 标题栏 */}
          <div className={cn(
            "px-3 py-2 font-medium border-b flex items-center justify-between",
            isSelected ? "bg-primary/5" : "bg-muted/30"
          )}>
            <div className="flex items-center gap-1 truncate">
              <span className="truncate">{displayTitle}</span>
            </div>
            <div className="flex items-center gap-1">
              {statusInfo.badge}
            </div>
          </div>
          
          {/* 内容区 */}
          <div className="p-3 space-y-2">
            {/* URL */}
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 dark:text-blue-400 truncate hover:underline flex items-center gap-1 max-w-full"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{item.url}</span>
                    </a>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{item.url}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            
            {/* 信息行 */}
            <div className="flex justify-between items-center">
              <div className={cn("text-xs flex items-center", statusInfo.color)}>
                {statusInfo.icon}
                <span>{statusInfo.text}</span>
              </div>
              {fileSize && (
                <div className="text-xs text-muted-foreground">
                  大小: {fileSize}
                </div>
              )}
            </div>
          </div>
          
          {/* 操作按钮 */}
          <div className="px-3 py-2 border-t bg-muted/20 flex justify-between items-center">
            <div>
              {!item.markdown && !item.loading && (
                <Button
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onFetch(item.id);
                  }}
                  size="sm"
                  variant="secondary"
                  className="text-xs h-8"
                >
                  开始解析
                </Button>
              )}
            </div>
            <div className="flex gap-1">
              {item.markdown && (
                <>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                          <CopyButton
                            value={item.markdown}
                            variant="outline"
                            size="sm"
                            className="text-xs h-8 flex items-center"
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>复制 Markdown</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Button
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      onDownload(item.id);
                    }}
                    variant="outline"
                    size="sm"
                    className="text-xs h-8"
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    下载
                  </Button>
                </>
              )}
              <Button
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onRemove(item.id);
                }}
                variant="destructive"
                size="sm"
                className="text-xs h-8"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                删除
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 